<template>
  <TheLoadingPage v-if="isLoaded === false || !isOnboardingPageDisplayed" />

  <BaseModal
    :is-open="isModalOpen"
    :icon="modal.icon"
    :icon-color="modal.color"
    :link-text="modal.linkText"
    :text-title="modal.title"
    :text="modal.text"
    @click="close"
  />

  <div v-if="isOnboardingPageDisplayed === true && isModalOpen === false" class="flex items-center justify-center">
    <ConfettiExplosion
      :particle-count="200"
      :particle-size="12"
      :force="1"
      :duration="3000"
      :stage-h="2000"
      :stage-width="1500"
      :colors="['#6856d2', '#d1a3de', '#f6d04f', '#41BBC7']"
    />
  </div>

  <div v-if="isOnboardingPageDisplayed === true" class="p-16">
    <h1 class="text-3xl font-extrabold tracking-tight text-foreground">
      {{ $t('pages.settings.accountPage.mandates.onboarding.title') }} {{ user?.firstname }} 👏
    </h1>
    <p class="text-md mt-1 hidden font-light text-foreground-secondary sm:block sm:text-lg">
      {{ $t('pages.settings.accountPage.mandates.onboarding.subtitle') }}
    </p>

    <div>
      <h2 class="mt-12 text-pretty text-2xl font-semibold tracking-tight text-foreground">
        {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.title') }}
      </h2>
      <div class="mt-6 border-b border-t bg-white p-6 sm:rounded-lg sm:border">
        <div class="grid grid-cols-1 text-sm sm:grid-cols-12 sm:grid-rows-1 sm:gap-x-6 md:gap-x-8 lg:gap-x-8">
          <div class="sm:col-span-4 md:col-span-5 md:row-span-2 md:row-end-2">
            <div class="h-full overflow-hidden rounded-lg bg-muted">
              <video
                autoplay
                muted
                loop
                :src="previewAlexHelloVideoUrl"
                alt="Bienvenue chez Hey Pongo !"
                class="h-full w-full object-cover object-center"
              />
            </div>
          </div>
          <div class="mt-6 sm:col-span-7 sm:mt-0 md:row-end-1">
            <h3 class="text-lg font-semibold tracking-tight text-foreground">
              {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.intro.title.iAm') }}
              <span class="font-bold text-primary">Alexandra</span>,
              {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.intro.title.illHelp') }}
              <span class="font-bold text-primary">{{ user?.merchant.name }}</span>
              {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.intro.title.toTheStars') }} ✨
            </h3>
            <p class="text-base text-muted-foreground">
              {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.intro.subtitle') }}
            </p>
          </div>
          <div class="sm:col-span-12 md:col-span-7">
            <dl class="grid grid-cols-1 gap-y-8 border-b py-8 sm:grid-cols-2 sm:gap-x-6 sm:py-6 md:py-10">
              <div v-if="deliveryAddress">
                <dt class="font-medium text-foreground">
                  {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.delivery.title') }}
                </dt>
                <dd class="mt-3 space-y-1 text-muted-foreground">
                  <span class="block"
                    >{{ deliveryAddress.streetNumber }} {{ deliveryAddress.streetType }}
                    {{ deliveryAddress.streetName }}</span
                  >
                  <span class="block">{{ deliveryAddress.zipCode }} {{ deliveryAddress.city }}</span>
                </dd>
              </div>
              <div>
                <dt class="font-medium text-foreground">
                  {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.haveQuestion') }}
                </dt>
                <dd class="mt-3 space-y-1 text-muted-foreground">
                  <p>
                    <a :href="`mailto:${supportEmailAddress}`">{{ supportEmailAddress }}</a>
                  </p>
                  <p><a href="tel:+33644647821">+33 6 44 64 78 21</a></p>
                  <a
                    target="_blank"
                    href="https://wa.me/33644647821"
                    class="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                  >
                    {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.sendWhatsapp') }}
                  </a>
                </dd>
              </div>
            </dl>
            <p v-if="onboardingDate" class="mt-6 font-medium text-foreground md:mt-10">
              {{ welcomeDeliveryDates }}
            </p>
            <div class="mt-6">
              <div class="overflow-hidden rounded-full bg-stone-200">
                <div class="h-2 rounded-full bg-primary" style="width: calc((1 * 2 + 1) / 8 * 100%)"></div>
              </div>
              <div class="mt-4 hidden grid-cols-4 font-medium text-foreground-secondary sm:grid">
                <div class="text-primary">
                  {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.delivery.statuses.mandateFilled') }}
                </div>
                <div class="text-center text-primary">
                  {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.delivery.statuses.boxPreparation') }}
                </div>
                <div class="text-center">
                  {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.delivery.statuses.delivery') }}
                </div>
                <div class="text-right">
                  {{ $t('pages.settings.accountPage.mandates.onboarding.welcome.delivery.statuses.delivered') }} ⚡️
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div>
      <h2 class="mt-12 text-pretty text-2xl font-semibold tracking-tight text-foreground">
        {{
          hasRewards || hasContest
            ? $t('pages.settings.accountPage.mandates.onboarding.preview.title')
            : $t('pages.settings.accountPage.mandates.onboarding.preview.titleNoPreview')
        }}
        😇
      </h2>
      <p class="text-lg font-light text-foreground">
        {{
          hasRewards || hasContest
            ? $t('pages.settings.accountPage.mandates.onboarding.preview.subtitle')
            : $t('pages.settings.accountPage.mandates.onboarding.preview.subtitleNoPreview')
        }}
        💫
      </p>
      <div class="mt-6 lg:grid lg:grid-cols-2 lg:gap-6">
        <div
          class="group flex cursor-pointer flex-col justify-between border-b border-t bg-white p-6 sm:rounded-lg sm:border"
        >
          <div class="flex flex-col justify-start">
            <div
              v-if="hasRewards"
              class="flex h-[480px] w-full items-center justify-center overflow-hidden bg-primary/5 p-6"
            >
              <Mockup type="mobile" :scale="0.58">
                <WebAppPreview :rewards="rewards" :color="bgColor" />
              </Mockup>
            </div>
            <img v-else class="h-full w-full object-cover" :src="previewWebAppImgUrl" />
            <div class="p-8">
              <div class="lg:flex lg:items-center lg:justify-between">
                <div class="pr-6">
                  <p class="mb-1 flex items-center text-base font-semibold text-foreground">
                    <span>{{
                      hasRewards
                        ? $t('pages.settings.accountPage.mandates.onboarding.preview.loyalty.seeProgram.title')
                        : $t('pages.settings.accountPage.mandates.onboarding.preview.loyalty.createProgram.title')
                    }}</span>
                  </p>
                  <p class="mb-2 line-clamp-4 text-sm text-foreground">
                    <span v-if="hasRewards">
                      <span class="hidden lg:inline">{{
                        $t('pages.settings.accountPage.mandates.onboarding.preview.loyalty.seeProgram.desktopSubtitle')
                      }}</span>
                      <span class="inline lg:hidden">{{
                        $t('pages.settings.accountPage.mandates.onboarding.preview.loyalty.seeProgram.mobileSubtitle')
                      }}</span>
                    </span>
                    <span v-else>
                      <span>{{
                        $t('pages.settings.accountPage.mandates.onboarding.preview.loyalty.createProgram.subtitle')
                      }}</span>
                    </span>
                  </p>
                  <div class="mt-6 text-primary lg:flex lg:flex-row lg:justify-start">
                    <Button
                      v-if="hasRewards"
                      variant="default-primary"
                      class="my-2 block lg:hidden"
                      @click="`https://${user?.merchant.webAppUrl}`"
                    >
                      <BaseIcon name="HeroEyeIconOutline" class="h-5 w-5" />
                      <span>{{
                        $t('pages.settings.accountPage.mandates.onboarding.preview.loyalty.seeProgram.buttons.see')
                      }}</span>
                    </Button>
                    <Button variant="outline" @click="editRewards">
                      <BaseIcon name="HeroPencilSquareIconOutline" class="h-5 w-5" />
                      <span>{{
                        hasRewards
                          ? $t('pages.settings.accountPage.mandates.onboarding.preview.loyalty.seeProgram.buttons.edit')
                          : $t(
                              'pages.settings.accountPage.mandates.onboarding.preview.loyalty.createProgram.buttons.create'
                            )
                      }}</span>
                    </Button>
                  </div>
                </div>
                <div v-if="hasRewards" class="hidden lg:block">
                  <div ref="downloadableQrcode" class="rounded-lg border border-border bg-white p-4">
                    <qrcode-vue :value="`https://${user?.merchant.webAppUrl}`" :size="100" level="H" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="group flex cursor-pointer flex-col justify-between border-b border-t bg-white p-6 sm:rounded-lg sm:border"
        >
          <div class="flex flex-col justify-start">
            <div
              v-if="hasContest"
              class="flex h-[480px] items-center justify-center overflow-hidden rounded-lg rounded-b-none bg-primary/5 p-6"
            >
              <ContestModalPreview
                v-if="contests[0].contestGameRewards"
                :hide-bottom="false"
                :scale="0.58"
                :detail-contests="contests[0]"
              />
            </div>
            <img v-else class="h-full w-full object-cover" :src="previewContestImgUrl" />
            <div class="p-8">
              <div class="lg:flex lg:items-center lg:justify-between">
                <div class="pr-6">
                  <p class="mb-1 flex items-center text-base font-semibold text-foreground">
                    <span>{{
                      hasContest
                        ? $t('pages.settings.accountPage.mandates.onboarding.preview.contest.seeContest.title')
                        : $t('pages.settings.accountPage.mandates.onboarding.preview.contest.createContest.title')
                    }}</span>
                  </p>
                  <p class="mb-2 line-clamp-4 text-sm text-foreground">
                    <span v-if="hasContest">
                      <span class="hidden lg:inline">{{
                        $t('pages.settings.accountPage.mandates.onboarding.preview.contest.seeContest.desktopSubtitle')
                      }}</span>
                      <span class="inline lg:hidden">{{
                        $t('pages.settings.accountPage.mandates.onboarding.preview.contest.seeContest.mobileSubtitle')
                      }}</span>
                    </span>
                    <span v-else>
                      <span>{{
                        $t('pages.settings.accountPage.mandates.onboarding.preview.contest.createContest.subtitle')
                      }}</span>
                    </span>
                  </p>
                  <div class="mt-6 text-primary lg:flex lg:flex-row lg:justify-start">
                    <Button
                      v-if="hasContest"
                      variant="default-primary"
                      class="my-2 block lg:hidden"
                      @click="contestGameUrl"
                    >
                      <BaseIcon name="HeroEyeIconOutline" class="h-5 w-5" />
                      <span>{{
                        $t('pages.settings.accountPage.mandates.onboarding.preview.contest.seeContest.buttons.see')
                      }}</span>
                    </Button>
                    <Button variant="outline" @click="editContest">
                      <BaseIcon name="HeroPencilSquareIconOutline" class="h-5 w-5" />
                      <span>{{
                        hasContest
                          ? $t('pages.settings.accountPage.mandates.onboarding.preview.contest.seeContest.buttons.edit')
                          : $t(
                              'pages.settings.accountPage.mandates.onboarding.preview.contest.createContest.buttons.create'
                            )
                      }}</span>
                    </Button>
                  </div>
                </div>
                <div v-if="hasContest" class="hidden lg:block">
                  <div ref="downloadableQrcode" class="rounded-lg border border-border bg-white p-4">
                    <qrcode-vue :value="contestGameUrl" :size="100" level="H" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div>
      <h2 class="mt-12 text-pretty text-2xl font-semibold tracking-tight text-foreground">
        {{ $t('pages.settings.accountPage.mandates.onboarding.box.title') }}
      </h2>
      <p class="text-lg font-light text-foreground">
        {{ $t('pages.settings.accountPage.mandates.onboarding.box.subtitle') }}
      </p>
      <div class="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-6">
        <div class="relative lg:col-span-3">
          <div class="absolute inset-px rounded-lg bg-white max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem]" />
          <div
            class="relative flex h-full flex-col overflow-hidden rounded-[calc(var(--radius-lg)+1px)] max-lg:rounded-t-[calc(2rem+1px)] lg:rounded-tl-[calc(2rem+1px)]"
          >
            <img class="h-80 object-cover object-left" :src="onboardingBoxImgUrl" alt="" />
            <div class="p-10">
              <h3 class="text-sm/4 font-semibold text-primary">
                {{ $t('pages.settings.accountPage.mandates.onboarding.box.step1.title') }}
              </h3>
              <p class="text-lg/7 font-medium tracking-tight text-foreground">
                {{ $t('pages.settings.accountPage.mandates.onboarding.box.step1.subtitle') }}
              </p>
              <p class="mt-2 max-w-lg text-sm/6 text-foreground-secondary">
                {{ $t('pages.settings.accountPage.mandates.onboarding.box.step1.description') }}
              </p>
            </div>
          </div>
          <div
            class="pointer-events-none absolute inset-px rounded-lg border max-lg:rounded-t-[2rem] lg:rounded-tl-[2rem]"
          />
        </div>
        <div class="relative lg:col-span-3">
          <div class="absolute inset-px rounded-lg bg-white lg:rounded-tr-[2rem]" />
          <div
            class="relative flex h-full flex-col overflow-hidden rounded-[calc(var(--radius-lg)+1px)] lg:rounded-tr-[calc(2rem+1px)]"
          >
            <img class="h-80 object-cover object-left lg:object-right" :src="onboardingCareCallImgUrl" alt="" />
            <div class="p-10">
              <h3 class="text-sm/4 font-semibold text-primary">
                {{ $t('pages.settings.accountPage.mandates.onboarding.box.step2.title') }}
              </h3>
              <p class="text-lg/7 font-medium tracking-tight text-foreground">
                {{ $t('pages.settings.accountPage.mandates.onboarding.box.step2.subtitle') }}
              </p>
              <p class="mt-2 max-w-lg text-sm/6 text-foreground-secondary">
                {{ $t('pages.settings.accountPage.mandates.onboarding.box.step2.description') }}
              </p>
              <div class="mt-6 flex flex-col gap-x-12 2xl:flex-row">
                <div class="flex">
                  <Phone class="mr-2 w-4 text-primary" /><span class="text-md font-light">+33 6 44 64 78 21</span>
                </div>
                <div class="flex">
                  <BaseIcon name="HeroEnvelopeIconSolid" class="mr-2 w-4 text-primary" /><span
                    class="text-md font-light"
                    >{{ supportEmailAddress }}</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="pointer-events-none absolute inset-px rounded-lg border lg:rounded-tr-[2rem]" />
        </div>
      </div>
    </div>

    <div>
      <h2 class="mt-12 text-pretty text-2xl font-semibold tracking-tight text-foreground">
        {{ $t('pages.settings.accountPage.mandates.onboarding.content.title') }}
      </h2>
      <p class="font-light text-foreground">
        {{ $t('pages.settings.accountPage.mandates.onboarding.content.subtitle') }}
      </p>

      <div class="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-6">
        <div class="relative lg:col-span-2">
          <div class="absolute inset-px rounded-lg bg-white lg:rounded-bl-[2rem]" />
          <div
            class="relative flex h-full flex-col overflow-hidden rounded-[calc(var(--radius-lg)+1px)] lg:rounded-bl-[calc(2rem+1px)]"
          >
            <img class="h-80 object-cover object-left" :src="onboardingImportImgUrl" alt="" />
            <div class="p-10 pt-6">
              <p class="mt-2 text-lg/7 font-medium tracking-tight text-foreground">
                {{ $t('pages.settings.accountPage.mandates.onboarding.content.import.title') }}
              </p>
              <p class="mt-2 max-w-lg text-sm/6 text-foreground-secondary">
                {{ $t('pages.settings.accountPage.mandates.onboarding.content.import.subtitle') }}
              </p>
              <Button variant="outline" class="mt-4" @click="importCustomers">
                <BaseIcon name="HeroArrowUpTrayIconOutline" class="h-5 w-5" />
                {{ $t('pages.settings.accountPage.mandates.onboarding.content.import.button') }}
              </Button>
            </div>
          </div>
          <div class="pointer-events-none absolute inset-px rounded-lg border lg:rounded-bl-[2rem]" />
        </div>
        <div class="relative lg:col-span-2">
          <div class="absolute inset-px rounded-lg bg-white" />
          <div class="relative flex h-full flex-col overflow-hidden rounded-[calc(var(--radius-lg)+1px)]">
            <img class="h-80 object-cover object-center" :src="onboardingTipsImgUrl" alt="" />
            <div class="p-10 pt-4">
              <p class="mt-2 text-lg/7 font-medium tracking-tight text-foreground">
                {{ $t('pages.settings.accountPage.mandates.onboarding.content.tips.title') }}
              </p>
              <p class="mt-2 max-w-lg text-sm/6 text-foreground-secondary">
                {{ $t('pages.settings.accountPage.mandates.onboarding.content.tips.subtitle') }}
              </p>
              <Button variant="outline" class="mt-4" @click="goToHome">
                <BaseIcon name="HeroEyeIconOutline" class="h-5 w-5" />
                {{ $t('pages.settings.accountPage.mandates.onboarding.content.tips.button') }}
              </Button>
            </div>
          </div>
          <div class="pointer-events-none absolute inset-px rounded-lg border" />
        </div>
        <div class="relative lg:col-span-2">
          <div class="absolute inset-px rounded-lg bg-white max-lg:rounded-b-[2rem] lg:rounded-br-[2rem]" />
          <div
            class="relative flex h-full flex-col overflow-hidden rounded-[calc(var(--radius-lg)+1px)] max-lg:rounded-b-[calc(2rem+1px)] lg:rounded-br-[calc(2rem+1px)]"
          >
            <img class="h-80 object-cover object-center" :src="onboardingFaqImgUrl" alt="" />
            <div class="p-10 pt-4">
              <p class="mt-2 text-lg/7 font-medium tracking-tight text-foreground">
                {{ $t('pages.settings.accountPage.mandates.onboarding.content.faq.title') }}
              </p>
              <p class="mt-2 max-w-lg text-sm/6 text-foreground-secondary">
                {{ $t('pages.settings.accountPage.mandates.onboarding.content.faq.subtitle') }}
              </p>
              <Button variant="outline" class="mt-4">
                <a class="flex" target="_blank" href="https://heypongohelp.zendesk.com/hc/fr">
                  <BaseIcon name="HeroQuestionMarkCircleIconOutline" class="h-5 w-5" />
                  {{ $t('pages.settings.accountPage.mandates.onboarding.content.faq.button') }}</a
                >
              </Button>
            </div>
          </div>
          <div
            class="pointer-events-none absolute inset-px rounded-lg border max-lg:rounded-b-[2rem] lg:rounded-br-[2rem]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Phone } from 'lucide-vue-next'
import { BaseModal } from '@/ui/dialog'
import axios from 'axios'
import QrcodeVue from 'qrcode.vue'

import { Button } from '@/ui/button'

import ConfettiExplosion from '@/components/ConfettiExplosion.vue'
import ContestModalPreview from '@/views/contests/Components/ContestModalPreview.vue'
import Mockup from '@/components/Mockup.vue'
import TheLoadingPage from '@/components/layout/TheLoadingPage.vue'
import WebAppPreview from '@/views/loyalty/components/WebAppPreview.vue'

import useLocalStorage from '@/composables/useLocalStorage'
import useReward from '@/composables/reward'

export default {
  components: {
    ConfettiExplosion,
    QrcodeVue,
    ContestModalPreview,
    WebAppPreview,
    Mockup,
    Button,
    BaseModal,
    Phone,
    TheLoadingPage
  },
  setup() {
    const { setupToken, user, onboardingDate } = useLocalStorage()
    const notification = useNotification()
    const { fetchMerchantRewards } = useReward()
    return {
      setupToken,
      notification,
      user,
      onboardingDate,
      fetchMerchantRewards
    }
  },
  data: () => {
    return {
      isLoaded: false,
      isModalOpen: false,
      isMandateSuccessful: null,
      mandateError: null,
      contestGameUrl: null,
      contests: [],
      rewards: [],
      merchantStores: [],
      deliveryAddress: null,
      firstMandateCreatedDate: null,
      bgColor: '#FFFFFF',
      modal: {
        isDisplayed: false,
        icon: 'HeroCheckIconSolid',
        iconColor: '#000000',
        linkText: '',
        title: '',
        text: ''
      }
    }
  },
  computed: {
    supportEmailAddress() {
      return 'support@heypongo.com'
    },
    isSingleStore() {
      return this.merchantStores.length === 1
    },
    hasRewards() {
      return this.rewards.length > 0
    },
    hasContest() {
      return this.contests.length > 0
    },
    isOnboardingPageDisplayed() {
      return (
        this.isSingleStore === true &&
        (this.isMandateSuccessful === true || this.onboardingDate.length > 0 || this.isLoaded === true)
      )
    },
    welcomeDeliveryDates() {
      return this.$t('pages.settings.accountPage.mandates.onboarding.welcome.delivery.dates', {
        startDate: new Date(
          new Date(this.onboardingDate).setDate(new Date(this.onboardingDate).getDate() + 3)
        ).getDate(),
        endDate: new Date(
          new Date(this.onboardingDate).setDate(new Date(this.onboardingDate).getDate() + 7)
        ).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long'
        })
      })
    },
    onboardingBaseUrl() {
      return `${import.meta.env.VITE_AWS_STORAGE_URL}/onboarding/new-onboarding/`
    },
    previewWebAppImgUrl() {
      return `${this.onboardingBaseUrl}preview-webapp.png`
    },
    previewAlexHelloVideoUrl() {
      return `${this.onboardingBaseUrl}alex-hello.mp4`
    },
    previewContestImgUrl() {
      return `${this.onboardingBaseUrl}preview-contest.png`
    },
    onboardingBoxImgUrl() {
      return `${this.onboardingBaseUrl}box.png`
    },
    onboardingCareCallImgUrl() {
      return `${this.onboardingBaseUrl}care-call.png`
    },
    onboardingImportImgUrl() {
      return `${this.onboardingBaseUrl}import.png`
    },
    onboardingTipsImgUrl() {
      return `${this.onboardingBaseUrl}tips.png`
    },
    onboardingFaqImgUrl() {
      return `${this.onboardingBaseUrl}faq-b.png`
    }
  },
  async created() {
    if ('redirect_flow_id' in this.$route.query) {
      await this.displayFlowModal()
    }

    Promise.all([
      this.fetchMerchantColors(),
      this.fetchMerchantStores(),
      this.fetchMerchantRewards(),
      this.fetchContests()
    ]).finally(() => {
      this.isLoaded = true
    })
  },
  methods: {
    close() {
      this.isModalOpen = false
      if (this.$route.query.return === 'payment-methods') {
        this.$router.push({ name: 'InvoiceList', query: { activeTab: 'payment-methods' } })
        return
      }
      if (this.isMandateSuccessful === false || this.isSingleStore === false) this.$router.push({ name: 'Home' })
    },
    async displayFlowModal() {
      await this.axios
        .get('/settings/gocardless/complete', {
          params: {
            flow: this.$route.query.redirect_flow_id
          }
        })
        .then((response) => {
          if (response.data?.data?.error) {
            this.isMandateSuccessful = false
            this.mandateError = response.data.data.error
          } else {
            this.isMandateSuccessful = true
            this.setupToken = ''
            if (!this.onboardingDate) this.onboardingDate = new Date().toISOString()
          }
          this.modal.icon = this.isMandateSuccessful ? 'HeroCheckIconSolid' : 'HeroExclamationCircleIconSolid'
          this.modal.color = this.isMandateSuccessful ? 'green' : 'red'
          this.modal.linkText = this.isMandateSuccessful
            ? this.$t('pages.settings.accountPage.mandates.result.success.button')
            : this.$t('global.words.close')
          this.modal.title = this.isMandateSuccessful
            ? this.$t('pages.settings.accountPage.mandates.result.success.onboarding')
            : this.$t('pages.settings.accountPage.mandates.result.error.title')
          this.modal.text = this.isMandateSuccessful
            ? this.$t('pages.settings.accountPage.mandates.result.success.description')
            : this.$t('pages.settings.accountPage.mandates.result.error.description') + this.mandateError
          this.isModalOpen = true
        })
        .catch(() => {
          this.isMandateSuccessful = false

          this.modal.icon = 'HeroExclamationCircleIconSolid'
          this.modal.color = 'red'
          this.modal.linkText = this.$t('global.words.close')
          this.modal.title = this.$t('pages.settings.accountPage.mandates.result.error.title')
          this.modal.text = this.$t('pages.settings.accountPage.mandates.error.contactPongo')

          this.isModalOpen = true
        })
    },
    editContest() {
      if (this.hasContest) {
        this.$router.push({
          name: 'ContestEdit',
          params: { contestId: this.contests[0].id }
        })
      } else {
        this.$router.push({
          name: 'ContestList',
          params: { isNew: true }
        })
      }
    },
    editRewards() {
      if (this.hasRewards) {
        this.$router.push({
          name: 'LoyaltyEdit'
        })
      } else {
        this.$router.push({
          name: 'LoyaltyCreate'
        })
      }
    },
    importCustomers() {
      this.$router.push({
        name: 'CustomerList'
      })
    },
    goToHome() {
      this.$router.push({
        name: 'Home'
      })
    },
    async fetchMerchantColors() {
      try {
        const { data: requestMerchantColors } = await axios.get(`merchants/${this.user.merchant.id}/colors`)
        if (requestMerchantColors?.data) {
          this.bgColor = requestMerchantColors.data.background
        }
      } catch (error) {
        this.bgColor = '#FFFFFF'
        throw error
      }
    },
    async fetchMerchantStores() {
      try {
        const { data: requestMerchantStores } = await axios.get('stores/', {
          params: { merchantId: this.user.merchant.id }
        })
        this.merchantStores = requestMerchantStores?.data || []
      } catch (error) {
        this.merchantStores = []
        throw error
      }
    },
    async fetchContests() {
      try {
        const { data: requestContests } = await axios.get('contest-games?page=1&per_page=1')
        this.contests = requestContests?.data || []
        this.contestGameUrl = `https://${this.contests[0]?.stores[0]?.shortLink?.shortLink}`
      } catch (error) {
        this.contests = []
        throw error
      }
    }
  }
}
</script>
